import React from "react";
import { Box, Typography } from "@mui/material";
import HPButton from "@/components/common/Button";
import type { DatasetMetric } from "@models/Metric";
import MetricBaseSummaryCell from "@/components/features/configuration/metrics/MetricBaseSummaryCell";
import {
  describeMetricFocus,
  formatMetricPopulationLabel,
  getMetricResultTypeLabel,
} from "@utils/metric-source-utils";

interface DatasetMetricSelectedSummaryProps {
  datasetId: string | null;
  metric: DatasetMetric;
  onChange: () => void;
}

/** Read-only summary of the selected Dataset Metric's saved definition — the
 *  metric itself is never editable from the Scorecard; researchers manage
 *  its calculation settings via Metrics in Dataset Configuration. Shown
 *  after selection, above the row's own Row Label + Brand Mappings. */
const DatasetMetricSelectedSummary: React.FC<DatasetMetricSelectedSummaryProps> = ({
  datasetId,
  metric,
  onChange,
}) => {
  const source = metric.source;

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ whiteSpace: "normal", wordBreak: "break-word", flex: 1 }}
        >
          {metric.name}
        </Typography>
        <HPButton variant="text" size="small" onClick={onChange}>
          Change
        </HPButton>
      </Box>

      {source && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Source question: {source.question.questionLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Result type: {getMetricResultTypeLabel(source.resultType)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Focus: {describeMetricFocus(source)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Metric population: {formatMetricPopulationLabel(metric.population)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Base:
            </Typography>
            {datasetId && <MetricBaseSummaryCell datasetId={datasetId} base={metric.base} />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DatasetMetricSelectedSummary;
